import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";

const Shell = ({ title, description, children }) => (
  <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
    <section className="w-full max-w-sm space-y-6 animate-rise">
      <div className="space-y-2">
        <img src="/organico-logo.svg" alt="Organico" className="h-10 w-10 rounded-lg" />
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{title}</h1>
        <p className="text-sm text-slate-600">{description}</p>
      </div>
      {children}
      <p className="text-center text-sm text-slate-600"><Link to="/login" className="font-medium text-slate-900 underline" data-testid="back-to-login-link">Torna all'accesso</Link></p>
    </section>
  </div>
);

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try { const { data } = await api.post("/auth/forgot-password", { email }); setSent(data.message); }
    catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };
  return (
    <Shell title="Password dimenticata" description="Inserisci l'email del tuo account: ti inviamo un link valido 1 ora per sceglierne una nuova.">
      {sent ? <p className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="forgot-sent">{sent}</p> : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <div className="space-y-1.5"><Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" /></div>
          {error && <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="forgot-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full" data-testid="forgot-submit-button">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Invia link</Button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) { setError("Le due password non coincidono"); return; }
    setError(""); setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password aggiornata: accedi con la nuova password");
      navigate("/login", { replace: true });
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };
  return (
    <Shell title="Scegli una nuova password" description="Almeno 8 caratteri con lettere e numeri.">
      {!token ? <p className="text-sm text-red-700" data-testid="reset-missing-token">Link non valido: richiedi un nuovo link da "Password dimenticata".</p> : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <div className="space-y-1.5"><Label htmlFor="pw">Nuova password</Label>
            <Input id="pw" type="password" required minLength={8} autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" /></div>
          <div className="space-y-1.5"><Label htmlFor="confirm">Ripeti password</Label>
            <Input id="confirm" type="password" required autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" /></div>
          {error && <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="reset-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full" data-testid="reset-submit-button">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salva nuova password</Button>
        </form>
      )}
    </Shell>
  );
}
